import {
  connectFirestoreEmulator,
  collection,
  CollectionReference,
  initializeFirestore,
  doc,
  DocumentReference,
} from 'firebase/firestore';
import type { Uid } from '../cross-app-shared/types/common';
import type {
  TaskGenerator,
  TaskTemplate,
  Task,
  ExternalTaskConcrete,
  ApplicableStandardTask,
  ComplianceFile,
  ComplianceFolder,
  AuditLogItem,
  NotificationLogItem,
  Role,
  Method9Values,
  TaskGeneratorBrief,
  Singleton,
  Notification,
  PermitIndex,
  PermitIndexCondition,
  ExternalIntegrationConfig,
} from '../compliance-shared/types/compliance';
import { computed, ref, type Ref, watch } from 'vue';
import { INTEGRATION_TEST } from '../cross-app-shared/services/automated-testing.ts';
import type { ComplianceQueueTask } from '../compliance-backend/compliance-queue';
import { app, loginState, uid } from '../cross-app-frontend/firebase';
import { tenantID } from '@/router/route-constants';
import { uidsWellKnown } from '../cross-app-shared/services/uids-well-known.ts';

// Can't use normal Firestore requests when running under Cypress.
// See: https://github.com/cypress-io/cypress/issues/2374#issuecomment-587879412
// See: https://stackoverflow.com/questions/59336720/cant-use-cypress-to-test-app-using-using-firestore-local-emulator
const db = initializeFirestore(app, { experimentalForceLongPolling: INTEGRATION_TEST }, `tenant-${tenantID}`);
if (FIREBASE_USING_EMULATORS) {
  connectFirestoreEmulator(db, 'localhost', 8080);
}
export const myUserId: Ref<Uid> = ref(uid);
export const isDeveloper = computed((): boolean => {
  if (!myUserId.value) {
    return false;
  }

  if (FIREBASE_USING_EMULATORS) {
    return true;
  }

  const uidsForEnv = uidsWellKnown(DEPLOYMENT_ENVIRONMENT);
  return [uidsForEnv.developerAccount, uidsForEnv.tylerChurch].includes(myUserId.value);
});

watch(
  loginState,
  (state) => {
    if (state === 'logged-in') {
      myUserId.value = uid;
    }
  },
  { immediate: true },
);

export const ComplianceDatabase = {
  CollectionTaskTemplate: collection(db, 'taskTemplates') as CollectionReference<TaskTemplate, TaskTemplate>,
  CollectionTaskGenerator: collection(db, 'taskGenerators') as CollectionReference<TaskGenerator, TaskGenerator>,
  CollectionTaskGeneratorBriefs: collection(db, 'taskGeneratorBriefs') as CollectionReference<
    TaskGeneratorBrief,
    TaskGeneratorBrief
  >,
  CollectionTask: collection(db, 'tasks') as CollectionReference<Task, Task>,
  CollectionTaskNotification: collection(db, 'taskNotifications') as CollectionReference<Notification, Notification>,
  CollectionExternalTasks: collection(db, 'externalTasks') as CollectionReference<
    ExternalTaskConcrete | ApplicableStandardTask,
    ExternalTaskConcrete | ApplicableStandardTask
  >,
  CollectionSingleton: {
    suggestedTaskCounts: doc(collection(db, 'singletons'), 'suggestedTaskCounts') as DocumentReference<
      Singleton.SuggestedTaskCounts,
      Singleton.SuggestedTaskCounts
    >,
    standardTasks: doc(collection(db, 'singletons'), 'standardTasks') as DocumentReference<
      Singleton.StandardTasks,
      Singleton.StandardTasks
    >,
  },
  CollectionFiles: collection(db, 'files') as CollectionReference<ComplianceFile, ComplianceFile>,
  CollectionFolders: collection(db, 'folders') as CollectionReference<ComplianceFolder, ComplianceFolder>,
  CollectionAuditLogs: collection(db, 'auditLogs') as CollectionReference<AuditLogItem, AuditLogItem>,
  CollectionNotificationLogs: collection(db, 'notificationLogs') as CollectionReference<
    NotificationLogItem,
    NotificationLogItem
  >,
  CollectionRoles: collection(db, 'roles') as CollectionReference<Role, Role>,
  CollectionMethod9: collection(db, 'method9') as CollectionReference<Method9Values, Method9Values>,
  CollectionBackendQueue: collection(db, 'backendQueueLog') as CollectionReference<
    ComplianceQueueTask,
    ComplianceQueueTask
  >,
  CollectionPermitsIndexing: collection(db, 'permitsIndexing') as CollectionReference<PermitIndex, PermitIndex>,
  CollectionPermitsIndexingConditions: collection(db, 'permitsIndexingConditions') as CollectionReference<
    PermitIndexCondition,
    PermitIndexCondition
  >,
  CollectionExternalIntegrationConfigs: collection(db, 'externalIntegrationConfigs') as CollectionReference<
    ExternalIntegrationConfig,
    ExternalIntegrationConfig
  >,
};
